/**
 * Left-top panel: documents tree (variation submissions only).
 *
 * The Workspace surface is purely about variations. Contract-corpus
 * uploads (head contract, special conditions, AS standards, schedule
 * of rates) live on the Contacts tab where the operator already
 * picks a contractor and a contract via the corpus picker; carrying
 * the same affordance here led to two parallel upload flows for
 * contract files and confused which one was authoritative. The
 * contract-corpus toggle, the base_contract gating block, and the
 * CONTRACT CORPUS tree group have all been removed from this panel
 * (2026-05). Variation submissions are still uploaded here, gated by
 * a contract picker so each variation files against an existing
 * contract.
 *
 * Uploads are POSTed to /v1/documents and the tree polls
 * /v1/documents on a refresh tick so the operator sees fresh
 * uploads land without a manual refresh.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Icon } from "../icons";
import {
  deleteDocument,
  listContractors,
  listContracts,
  listDocuments,
  loadConfig,
  uploadDocument,
  type ApiError,
  type ContractOut,
  type ContractorOut,
  type DocumentRecord,
} from "../api/client";
import { useToast } from "../Toasts";
import { FileViewerModal } from "./FileViewerModal";

const VARIATION_DOC_TYPE = "variation_submission";
// Supporting documents (e.g. timesheets) attached to a variation
// (S16, ADR-0016). They join the contract corpus on upload and are
// flagged in this panel with the variation they belong to.
const SUPPORTING_DOC_TYPE = "supporting";
const VARIATION_HINT = "Notices, quotes, daywork sheets, supporting docs";

export function classifyDocType(value: string): "variation" | "supporting" | "other" {
  const cleaned = value.trim().toLowerCase().replace(/-/g, "_");
  if (cleaned === "variation_submission" || cleaned === "daywork_sheet") {
    return "variation";
  }
  if (cleaned === "supporting") {
    return "supporting";
  }
  return "other";
}

function bytesLabel(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const POLL_INTERVAL_MS = 4000;

export function DocumentsPanel({
  selectedDocId,
  onSelectDocument,
}: {
  selectedDocId: string | null;
  onSelectDocument: (id: string) => void;
}) {
  const toast = useToast();
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [docs, setDocs] = useState<DocumentRecord[]>([]);
  const [refreshTick, setRefreshTick] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [viewerDoc, setViewerDoc] = useState<DocumentRecord | null>(null);
  // The variation submission a supporting-document upload is being
  // attached to (S16). Set when the operator clicks a variation's
  // attach control; consumed by the hidden supporting-file input.
  const [attachTarget, setAttachTarget] = useState<DocumentRecord | null>(null);

  // Variation uploads need an existing contract (the document is
  // bound to it so the engine and contacts surface know what it
  // belongs to). The contract picker is the only gating widget on
  // this panel post-2026-05; contract uploads are managed from the
  // Contacts tab.
  const [contractors, setContractors] = useState<ContractorOut[]>([]);
  const [contracts, setContracts] = useState<ContractOut[]>([]);
  const [selectedContractId, setSelectedContractId] = useState<string>("");

  // Keep the contract picker fresh. The poll cadence matches the
  // documents poll so a contract added from the Contacts tab in
  // another window appears here within a few seconds.
  useEffect(() => {
    let cancelled = false;
    const cfg = loadConfig();
    async function refresh() {
      try {
        const [crs, cts] = await Promise.all([
          listContractors(cfg),
          listContracts(cfg),
        ]);
        if (cancelled) return;
        setContractors(crs);
        setContracts(cts);
      } catch {
        // The Contacts surface raises its own errors; here we
        // keep the last known state and let the operator carry on.
      }
    }
    void refresh();
    const handle = window.setInterval(() => void refresh(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(handle);
    };
  }, [refreshTick]);

  // Auto-select the first contract when the list loads or refreshes;
  // preserve the operator's pick if it is still in the list.
  useEffect(() => {
    setSelectedContractId((current) => {
      if (current && contracts.some((c) => c.id === current)) {
        return current;
      }
      return contracts[0]?.id ?? "";
    });
  }, [contracts]);

  // Fetch + poll the tenant's documents.
  useEffect(() => {
    let cancelled = false;
    const cfg = loadConfig();
    listDocuments(cfg)
      .then((list) => {
        if (!cancelled) {
          setDocs(list);
          setLoadError(null);
        }
      })
      .catch((err: ApiError) => {
        if (!cancelled) setLoadError(err.message ?? "Could not load documents");
      });
    const handle = window.setInterval(() => {
      if (cancelled) return;
      listDocuments(cfg)
        .then((list) => {
          if (!cancelled) setDocs(list);
        })
        .catch(() => {
          /* keep last known docs on transient errors */
        });
    }, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(handle);
    };
  }, [refreshTick]);

  const ingest = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;
      // Belt-and-braces guard against a keyboard / drag path that
      // bypasses the disabled flag on the dropzone.
      if (!selectedContractId) {
        toast.show(
          "err",
          "Pick a contract first. Variations have to be filed against one.",
        );
        return;
      }
      setBusy(true);
      const cfg = loadConfig();
      try {
        const fresh: DocumentRecord[] = [];
        for (const file of files) {
          const record = await uploadDocument(cfg, file, VARIATION_DOC_TYPE, {
            contractId: selectedContractId,
          });
          fresh.push(record);
        }
        setDocs((prev) => [...fresh, ...prev]);
        setRefreshTick((k) => k + 1);
        toast.show(
          "ok",
          `Uploaded ${fresh.length} variation submission${fresh.length === 1 ? "" : "s"}.`,
        );
      } catch (err) {
        const apiErr = err as ApiError;
        toast.show("err", `Upload failed: ${apiErr.message ?? "unknown error"}`);
      } finally {
        setBusy(false);
      }
    },
    [selectedContractId, toast],
  );

  // Attach supporting documents to a specific variation (S16,
  // ADR-0016). They upload with doc_type="supporting" and
  // variation_ref set to the variation submission's id, scoped to the
  // same contract as that variation so they join its contract corpus.
  const attachSupporting = useCallback(
    async (variation: DocumentRecord, files: File[]) => {
      if (files.length === 0) return;
      const contractId = variation.contract_id;
      if (!contractId) {
        toast.show(
          "err",
          "That variation has no contract; cannot attach supporting documents.",
        );
        return;
      }
      setBusy(true);
      const cfg = loadConfig();
      try {
        const fresh: DocumentRecord[] = [];
        for (const file of files) {
          const record = await uploadDocument(cfg, file, SUPPORTING_DOC_TYPE, {
            contractId,
            variationRef: variation.document_id,
          });
          fresh.push(record);
        }
        setDocs((prev) => [...fresh, ...prev]);
        setRefreshTick((k) => k + 1);
        toast.show(
          "ok",
          `Attached ${fresh.length} supporting document${fresh.length === 1 ? "" : "s"} to ${variation.original_filename}.`,
        );
      } catch (err) {
        const apiErr = err as ApiError;
        toast.show("err", `Attach failed: ${apiErr.message ?? "unknown error"}`);
      } finally {
        setBusy(false);
        setAttachTarget(null);
      }
    },
    [toast],
  );

  const onDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDrag(false);
    void ingest(Array.from(event.dataTransfer.files ?? []));
  };

  const onSupportingSelected = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const target = attachTarget;
    const files = Array.from(event.target.files ?? []);
    event.target.value = "";
    if (target) void attachSupporting(target, files);
  };

  const onSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    void ingest(Array.from(event.target.files ?? []));
    event.target.value = "";
  };

  const groups = useMemo(() => {
    const variation: DocumentRecord[] = [];
    const supporting: DocumentRecord[] = [];
    const other: DocumentRecord[] = [];
    for (const doc of docs) {
      // Hide contract-corpus documents from the Workspace tree.
      // They live on the Contacts tab; keeping them out of the
      // Workspace stops the panel from looking like there are
      // two parallel filing systems.
      const cls = classifyDocType(doc.doc_type ?? "");
      if (cls === "variation") variation.push(doc);
      else if (cls === "supporting") supporting.push(doc);
      else if ((doc.doc_type ?? "").trim() === "") other.push(doc);
      // Anything else (base_contract, standard, rate_table, drawing,
      // explainer) is intentionally dropped from this view.
    }
    return { variation, supporting, other };
  }, [docs]);

  // Resolve a variation_ref (a variation submission's document id) to
  // its filename so supporting documents can be flagged with the
  // variation they belong to (S16).
  const variationNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const doc of docs) map.set(doc.document_id, doc.original_filename);
    return map;
  }, [docs]);

  const visibleGroup = (group: DocumentRecord[]): DocumentRecord[] => {
    if (!search.trim()) return group;
    const needle = search.toLowerCase();
    return group.filter((d) => d.original_filename.toLowerCase().includes(needle));
  };

  const onDelete = async (doc: DocumentRecord): Promise<void> => {
    const ok = window.confirm(
      `Delete ${doc.original_filename}? Its indexed chunks will be removed too; new determinations will not see it.`,
    );
    if (!ok) return;
    const cfg = loadConfig();
    try {
      await deleteDocument(cfg, doc.document_id);
      setDocs((prev) => prev.filter((d) => d.document_id !== doc.document_id));
      toast.show("ok", `Deleted ${doc.original_filename}.`);
    } catch (err) {
      const apiErr = err as ApiError;
      toast.show("err", `Delete failed: ${apiErr.message ?? "unknown"}`);
    }
  };

  const renderRows = (
    group: DocumentRecord[],
    tone: "variation" | "supporting" | "other",
    opts?: {
      // Variation rows: a control to attach supporting documents to
      // that variation (S16). Supporting rows: a resolver that flags
      // each document with the variation it belongs to.
      onAttach?: (doc: DocumentRecord) => void;
      badgeFor?: (doc: DocumentRecord) => string | null;
    },
  ): React.ReactNode => {
    if (group.length === 0) {
      return (
        <div className="empty-compact tree-empty">
          <p>No documents in this group yet.</p>
        </div>
      );
    }
    return group.map((doc) => {
      const flag = opts?.badgeFor?.(doc) ?? null;
      return (
        <div
          key={doc.document_id}
          className={`file file-${tone}`}
          aria-current={selectedDocId === doc.document_id || undefined}
        >
          <button
            type="button"
            className="file-row-main"
            onClick={() => {
              onSelectDocument(doc.document_id);
              setViewerDoc(doc);
            }}
            title={`Preview ${doc.original_filename}`}
          >
            <Icon.file className="file-icon" />
            <span className="file-name">{doc.original_filename}</span>
            {flag && (
              <span className="file-flag" title={`Attached to ${flag}`}>
                ↳ {flag}
              </span>
            )}
            <span className="file-meta">{bytesLabel(doc.byte_size)}</span>
          </button>
          {opts?.onAttach && (
            <button
              type="button"
              className="file-attach"
              aria-label={`Attach supporting document to ${doc.original_filename}`}
              title="Attach a supporting document to this variation"
              onClick={() => opts.onAttach?.(doc)}
            >
              <Icon.attachment />
            </button>
          )}
          <button
            type="button"
            className="file-delete"
            aria-label={`Delete ${doc.original_filename}`}
            title={`Delete ${doc.original_filename}`}
            onClick={() => void onDelete(doc)}
          >
            <Icon.cross />
          </button>
        </div>
      );
    });
  };

  const ungated = !selectedContractId;

  return (
    <div className="docs-panel" aria-label="Documents">
      <div className="col-head">
        <div className="col-title">DOCUMENTS</div>
        <button
          className="btn btn-primary btn-sm docs-add"
          type="button"
          aria-label="Add document"
          disabled={ungated}
          title={ungated ? "Pick a contract first" : "Add a document"}
          onClick={() => {
            if (ungated) {
              toast.show("err", "Pick a contract first.");
              return;
            }
            document.getElementById("docs-input")?.click();
          }}
        >
          <Icon.plus />
          Add document
        </button>
      </div>

      <div className="docs-gate">
        <label htmlFor="docs-contract" className="docs-gate-label">
          Contract
        </label>
        <select
          id="docs-contract"
          value={selectedContractId}
          onChange={(e) => setSelectedContractId(e.target.value)}
          disabled={contracts.length === 0}
        >
          {contracts.length === 0 && (
            <option value="">No contracts — add one from the Contacts tab</option>
          )}
          {contracts.map((c) => {
            const contractor = contractors.find(
              (cr) => cr.id === c.contractor_id,
            );
            return (
              <option key={c.id} value={c.id}>
                {contractor ? `${contractor.name} · ` : ""}
                {c.reference}
              </option>
            );
          })}
        </select>
      </div>

      <div
        className={`drop drop-compact ${drag ? "dragover" : ""}${ungated ? " drop-disabled" : ""}`}
        onDragOver={(e) => {
          if (ungated) return;
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          if (ungated) return;
          onDrop(e);
        }}
        role="button"
        tabIndex={ungated ? -1 : 0}
        aria-label="Drop variation submissions here"
        aria-disabled={ungated}
        onKeyDown={(e) => {
          if (ungated) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            document.getElementById("docs-input")?.click();
          }
        }}
        onClick={() => {
          if (ungated) {
            toast.show("err", "Pick a contract first.");
            return;
          }
          document.getElementById("docs-input")?.click();
        }}
      >
        <Icon.upload className="drop-icon" />
        <div className="drop-title">
          {busy
            ? "Uploading…"
            : ungated
              ? "Pick a contract first"
              : "Drop files here"}
        </div>
        <div className="drop-sub">
          → <strong>variation submissions</strong>
        </div>
        <div className="drop-sub drop-formats">{VARIATION_HINT}</div>
        <input
          id="docs-input"
          type="file"
          multiple
          style={{ display: "none" }}
          onChange={onSelect}
          disabled={ungated}
        />
      </div>

      <div className="search">
        <Icon.search />
        <input
          type="search"
          placeholder="Search documents…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {loadError && (
        <div className="empty-compact tree-empty" style={{ color: "var(--err)" }}>
          <p>{loadError}</p>
        </div>
      )}

      <div className="files">
        <div className="tree-group">
          <div className="tree-h">
            <div className="tree-h-l">VARIATION SUBMISSIONS</div>
            <div className="tree-h-r">{groups.variation.length}</div>
          </div>
          {renderRows(visibleGroup(groups.variation), "variation", {
            onAttach: (doc) => {
              // Stash the target variation, then open the supporting-
              // file picker. The selection handler reads attachTarget.
              setAttachTarget(doc);
              window.setTimeout(
                () => document.getElementById("supporting-input")?.click(),
                0,
              );
            },
          })}
        </div>

        {groups.supporting.length > 0 && (
          <div className="tree-group">
            <div className="tree-h">
              <div className="tree-h-l">SUPPORTING DOCUMENTS</div>
              <div className="tree-h-r">{groups.supporting.length}</div>
            </div>
            {renderRows(visibleGroup(groups.supporting), "supporting", {
              badgeFor: (doc) =>
                doc.variation_ref
                  ? (variationNameById.get(doc.variation_ref) ?? "variation removed")
                  : null,
            })}
          </div>
        )}

        {groups.other.length > 0 && (
          <div className="tree-group">
            <div className="tree-h">
              <div className="tree-h-l">OTHER</div>
              <div className="tree-h-r">{groups.other.length}</div>
            </div>
            {renderRows(visibleGroup(groups.other), "other")}
          </div>
        )}
      </div>

      {/* Hidden picker for supporting-document attachments. The
          variation it attaches to is whichever row's attach control
          was clicked (attachTarget). */}
      <input
        id="supporting-input"
        type="file"
        multiple
        style={{ display: "none" }}
        onChange={onSupportingSelected}
      />

      {viewerDoc && (
        <FileViewerModal
          doc={viewerDoc}
          onClose={() => setViewerDoc(null)}
        />
      )}
    </div>
  );
}
